import {
    AssertionDescriptorDisplayValueTypeEnum,
    AssertionDescriptorValueSourceTypeEnum,
} from '../../utilities/Constants';
import Common, { EMPTY_VALUE } from '../../stores/Common';
import { ICaseAssertionModel, ICaseModel } from '../../interfaces/ICase';
import {
    SameCaseApprovedHelperText,
    SameCasePendingHelperText,
} from '../../pages/quad/same-cases-admin/manage/useGetSameCasesQuery';

import { AssertionDescriptorInputControl } from './AssertionDescriptorInputControl';
import CaseFileCheckmateSelectHelper from '../../utilities/CaseFileCheckmateSelectHelper';
import CaseHelper from '../../utilities/CaseHelper';
import { FieldControlWrapper } from './FieldControlWrapper';
import { IAssertionDescriptorModel } from '../../interfaces/IAssertionDescriptor';
import { IDisplayFieldModel } from '../../interfaces/IDisplaySectionModel';
import { IMultiSelectOption } from '../../interfaces/ILookup';
import { ReactElement } from 'react';
import { lockableAssertionDescriptors } from '../../pages/quad/SameCaseHelper';
import { useCaseFileContext } from '../../contexts/useCaseFileContext';

interface IAssertionDescriptorFieldRendererProps {
    displayAsCondensed: boolean;
    field: IDisplayFieldModel;
    tabIndex?: number;

    assertionDescriptors: IAssertionDescriptorModel[];
    currentCase: ICaseModel;
    isReadOnlyMode: boolean;
    onChangeADTextValue: (
        event: React.ChangeEvent<HTMLInputElement> | React.ChangeEvent<HTMLSelectElement>
    ) => void;
    onAssertionDescriptorSearchableSingleSelectChange: (
        selectedOption: IMultiSelectOption,
        assertionDescriptorId: string,
        isBoolean?: boolean | undefined
    ) => void;
    onAssertionDescriptorSearchableMultiSelectChange: (
        selectedOptions: IMultiSelectOption[],
        assertionDescriptorId: string
    ) => void;
    setCurrentNarrativeCaseAssertion: React.Dispatch<
        React.SetStateAction<ICaseAssertionModel | null>
    >;
}

export function AssertionDescriptorFieldRenderer(props: IAssertionDescriptorFieldRendererProps) {
    const { isCaseApprovedInSameCaseGroup, isCasePendingInSameCaseGroup } = useCaseFileContext();

    const foundAD = props.assertionDescriptors.find(
        (ad) => ad.guid === props.field.assertionDescriptorGuid
    );

    const readonly =
        lockableAssertionDescriptors.includes(
            props.field.assertionDescriptorGuid.toUpperCase() || ''
        ) &&
        (isCaseApprovedInSameCaseGroup || isCasePendingInSameCaseGroup);

    let additionalStyleClass = '';

    const caseAssertionsForAD = props.currentCase.caseAssertions?.filter(
        (assertion) => assertion.assertionDescriptorGuid === props.field.assertionDescriptorGuid
    );

    const firstCaseAssertion = props.currentCase.caseAssertions?.find(
        (x) => x.assertionDescriptorGuid === props.field.assertionDescriptorGuid
    );

    if (foundAD) {
        const setStyleAsSystemAutoPopulated = CaseHelper.setStyleAsSystemAutoPopulated(
            firstCaseAssertion ?? {
                assertionDescriptorGuid: foundAD.guid,
            },
            props.currentCase
        );
        additionalStyleClass = setStyleAsSystemAutoPopulated ? ' font-green' : '';
    }

    let component: ReactElement | null;

    if (!foundAD) {
        component = <span style={{ fontWeight: '600', color: 'red' }}>AD Not Found</span>;
    } else if (props.isReadOnlyMode) {
        if (
            foundAD?.valueSourceType?.id === AssertionDescriptorValueSourceTypeEnum.Selection.Value
        ) {
            let label: string | undefined = '';
            if (
                foundAD.valueDisplayType?.id ===
                AssertionDescriptorDisplayValueTypeEnum.MultiSelect.Value
            ) {
                label = CaseFileCheckmateSelectHelper.getCheckmateNSelectValue(
                    caseAssertionsForAD ?? [],
                    props.assertionDescriptors
                )
                    ?.map((data) => data.label)
                    .join(' | ');
            } else {
                label = CaseFileCheckmateSelectHelper.getCheckmateSelectValue(
                    firstCaseAssertion ?? {},
                    props.assertionDescriptors
                )?.label;
            }

            component = label ? (
                <span className={additionalStyleClass}>{label}</span>
            ) : (
                <EmptyLabel />
            );
        } else {
            component = firstCaseAssertion ? (
                <span className={additionalStyleClass}>
                    {getFormattedText(firstCaseAssertion, props.assertionDescriptors)}
                </span>
            ) : (
                <EmptyLabel />
            );
        }
    } else {
        component = (
            <AssertionDescriptorInputControl
                currentCase={props.currentCase}
                assertionDescriptors={props.assertionDescriptors}
                assertionDescriptor={foundAD}
                onChangeADTextValue={props.onChangeADTextValue}
                onSearchableSingleSelectChange={
                    props.onAssertionDescriptorSearchableSingleSelectChange
                }
                onSearchableMultiSelectChange={
                    props.onAssertionDescriptorSearchableMultiSelectChange
                }
                onSelectNarrativeCaseAssertion={(ca: ICaseAssertionModel) =>
                    props.setCurrentNarrativeCaseAssertion(ca)
                }
                tabIndex={props.tabIndex}
                readonly={!!readonly}
            />
        );
    }

    const sameCaseHelpText = isCaseApprovedInSameCaseGroup
        ? SameCaseApprovedHelperText
        : isCasePendingInSameCaseGroup
          ? SameCasePendingHelperText
          : '';

    const sameCaseHelp =
        !props.isReadOnlyMode && readonly
            ? { text: sameCaseHelpText, title: foundAD?.displayName ?? '' }
            : undefined;

    const help = foundAD?.helpText
        ? { text: foundAD?.helpText, title: foundAD.displayName || '' }
        : sameCaseHelp;

    return (
        <FieldControlWrapper
            component={component}
            displayAsCondensed={props.displayAsCondensed && props.isReadOnlyMode}
            labelText={foundAD?.displayName || foundAD?.name || ''}
            identifier={props.field.assertionDescriptorGuid}
            help={help}
        />
    );
}

const getFormattedText = (
    caseAssertion: ICaseAssertionModel,
    assertionDescriptors: IAssertionDescriptorModel[]
) => {
    const found = assertionDescriptors.find(
        (ad) => ad.guid === caseAssertion.assertionDescriptorGuid
    );

    if (!found) {
        return '';
    }

    switch (found?.valueSourceType?.id) {
        case AssertionDescriptorValueSourceTypeEnum.Text.Value:
            switch (found?.valueDisplayType?.id) {
                case AssertionDescriptorDisplayValueTypeEnum.DateTime.Value:
                    return Common.safeDateFormatWithDependant(
                        caseAssertion.dateTimeValue?.toString(),
                        caseAssertion.dateTimeValue
                    );
                case AssertionDescriptorDisplayValueTypeEnum.Number.Value:
                case AssertionDescriptorDisplayValueTypeEnum.Decimal.Value:
                    return caseAssertion.numericValue;
                case AssertionDescriptorDisplayValueTypeEnum.Percentage.Value:
                    return caseAssertion.numericValue
                        ? caseAssertion.numericValue.toString() + '%'
                        : '';
                case AssertionDescriptorDisplayValueTypeEnum.Currency.Value:
                    return caseAssertion.numericValue
                        ? Common.formatCurrency(caseAssertion.numericValue.toString())
                        : '';
                case AssertionDescriptorDisplayValueTypeEnum.CurrencyWithCents.Value:
                    return caseAssertion.numericValue
                        ? Common.formatCurrencyWithCents(caseAssertion.numericValue.toString())
                        : '';
                case AssertionDescriptorDisplayValueTypeEnum.ShortText.Value:
                case AssertionDescriptorDisplayValueTypeEnum.Narrative.Value:
                case AssertionDescriptorDisplayValueTypeEnum.SSN.Value:
                    return caseAssertion.textValue;
                default:
                    break;
            }
            break;
        case AssertionDescriptorValueSourceTypeEnum.Boolean.Value:
            return caseAssertion?.booleanValue === true
                ? 'Yes'
                : caseAssertion?.booleanValue === false
                  ? 'No'
                  : '';
        default:
            break;
    }

    return '';
};

function EmptyLabel() {
    return <span>{EMPTY_VALUE}</span>;
}

interface ICaseAssertionDescriptorFieldRendererProps {
    displayAsCondensed: boolean;
    field: IDisplayFieldModel;
    tabIndex?: number;
}

export function CaseAssertionDescriptorFieldRenderer(
    props: ICaseAssertionDescriptorFieldRendererProps
) {
    const {
        assertionDescriptors,
        currentCase,
        isReadOnlyMode,
        handleChangeADTextValue,
        handleAssertionDescriptorSearchableSingleSelectChange,
        handleAssertionDescriptorSearchableMultiSelectChange,
        setCurrentNarrativeCaseAssertion,
    } = useCaseFileContext();

    return (
        <AssertionDescriptorFieldRenderer
            assertionDescriptors={assertionDescriptors}
            currentCase={currentCase}
            displayAsCondensed={props.displayAsCondensed}
            field={props.field}
            onAssertionDescriptorSearchableSingleSelectChange={
                handleAssertionDescriptorSearchableSingleSelectChange
            }
            onAssertionDescriptorSearchableMultiSelectChange={
                handleAssertionDescriptorSearchableMultiSelectChange
            }
            onChangeADTextValue={handleChangeADTextValue}
            isReadOnlyMode={isReadOnlyMode}
            setCurrentNarrativeCaseAssertion={setCurrentNarrativeCaseAssertion}
            tabIndex={props.tabIndex}
        />
    );
}
